/// <amd-module name="Build/Admin/Pool/Components/AgentList" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";

import { Ago } from "VSSUI/Ago";
import { Link, ILinkProps } from "VSSUI/Link";
import { IMenuItem, IMenuProps } from "VSSUI/Menu";
import { Table, ITableColumn, ITableRow, SimpleTableCell, TableColumnStyle, ColumnMore, TwoLineTableCell } from "VSSUI/Table";
import { Toggle } from "VSSUI/Toggle";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

import { TaskAgent, TaskAgentPool, TaskAgentStatus } from "DistributedTask/Client/Contracts/TaskAgent";

import { FwLinks } from "Build/Admin/Common/Constants";
import { isHostedOrPoolProviderBacked } from "Build/Admin/Common/TaskAgentPoolUtils";

import * as Resources from "Build/Admin/Pool/Resources";

export interface IAgentListProps {
    agents: TaskAgent[];
    agentLinkProvider(agent: TaskAgent): string | undefined;
    onAgentEnabledChanged(agent: TaskAgent, checked: boolean): void;
    onUpdateAgent(agent: TaskAgent): void;
    onDeleteAgent(agent: TaskAgent): void;
    pageContext: IVssPageContext;
    selectedAgentPool: TaskAgentPool | undefined;
}

class TableListItem {
    agent: TaskAgent;
    linkProps: ILinkProps;
}

export class AgentList extends React.PureComponent<IAgentListProps> {

    public render(): JSX.Element {
        const listItems = this._buildListItems();

        return (
            <div>
            {
                listItems.length > 0 &&
                <Table<TableListItem>
                    className="pipelines-agents-list"
                    columns={this._getColumns()}
                    itemProvider={new ArrayItemProvider<TableListItem>(listItems)}
                    onActivate={this._onRowClicked}
                />
            }
            {
                listItems.length === 0 &&
                <div className="fontSizeM">{Resources.AgentListEmptyExperience}</div>
            }
            </div>
        );
    }

    private _buildListItems(): TableListItem[] {
        let items: TableListItem[] = []

        if (this.props.agents) {
            this.props.agents.forEach(agent => {
                const linkProps: ILinkProps = { href: this.props.agentLinkProvider(agent) };
                items.push({ agent, linkProps });
            })
        }

        return items;
    }

    private _onRowClicked = (event: React.SyntheticEvent<HTMLElement>, row: ITableRow<TableListItem>): void => {
        if (row.data.linkProps && row.data.linkProps.href) {
            onClickFPS(this.props.pageContext, row.data.linkProps.href, true, event);
        }
    }

    private _getColumns(): ITableColumn<TableListItem>[] {
        const columns: ITableColumn<TableListItem>[] = [
            {
                id: "name",
                name: Resources.Name,
                width: new ObservableValue(-28),
                columnStyle: TableColumnStyle.Primary,
                renderCell: this._renderNameCell
            },
            {
                id: "lastRun",
                name: Resources.LastRun,
                width: new ObservableValue(-20),
                columnStyle: TableColumnStyle.Secondary,
                renderCell: this._renderLastRunCell
            },
            {
                id: "status",
                name: Resources.Status,
                width: new ObservableValue(-20),
                columnStyle: TableColumnStyle.Secondary,
                renderCell: this._renderStatusCell
            },
            {
                id: "version",
                name: Resources.Version,
                width: new ObservableValue(-20),
                columnStyle: TableColumnStyle.Secondary,
                renderCell: this._renderVersionCell
            },
            {
                id: "enabled",
                name: Resources.Enabled,
                width: new ObservableValue(-12),
                columnStyle: TableColumnStyle.Tertiary,
                renderCell: this._renderEnabledCell
            }
        ];

        if (this.props.selectedAgentPool && !isHostedOrPoolProviderBacked(this.props.selectedAgentPool)) {
            columns.push(new ColumnMore<TableListItem>(this._getContextMenuItems));
        }

        return columns;
    }

    private _getContextMenuItems = (listItem: TableListItem): IMenuProps => {
        const items: IMenuItem[] = [];

        items.push(
            {
                id: "update",
                text: Resources.UpdateAgent,
                onActivate: () => {
                    this.props.onUpdateAgent(listItem.agent);
                }
            } as IMenuItem
        );

        items.push(
            {
                id: "delete",
                text: Resources.Delete,
                onActivate: () => {
                    this.props.onDeleteAgent(listItem.agent);
                }
            } as IMenuItem
        );

        return {
            id: "agent-menu",
            items
        };
    }

    private _renderNameCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <TwoLineTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={"col-" + columnIndex}
                line1={this._renderPrimaryNameRowText(tableItem.agent)}
                line2={this._renderSecondaryNameRowText(tableItem.agent)}
            />
        );
    }

    private _renderPrimaryNameRowText(agent: TaskAgent): JSX.Element {
        return (
            <Tooltip overflowOnly={true}>
                <span className="fontWeightSemiBold fontSizeM text-ellipsis">
                    {agent.name}
                </span>
            </Tooltip>
        );
    }

    private _renderSecondaryNameRowText(agent: TaskAgent): JSX.Element {
        return (
            <span className="fontSize secondary-text flex-row flex-baseline text-ellipsis">
                {this._getStateIcon(agent)}
                {this._getStateText(agent)}
            </span>
        );
    }

    private _getStateIcon(agent: TaskAgent): JSX.Element | undefined {
        // Hosted agents are always online
        if (this.props.selectedAgentPool && isHostedOrPoolProviderBacked(this.props.selectedAgentPool)) {
            return <div className="agent-icon online" />;
        }

        if (agent.status === TaskAgentStatus.Online) {
            return <div className="agent-icon online" />;
        }
        else {
            return <div className="agent-icon offline" />;
        }
    }

    private _getStateText(agent: TaskAgent): string {
        // Hosted agents are always online
        if (this.props.selectedAgentPool && isHostedOrPoolProviderBacked(this.props.selectedAgentPool))  {
            return Resources.Online;
        }

        switch (agent.status) {
            case TaskAgentStatus.Offline:
                return Resources.Offline;
            case TaskAgentStatus.Online:
                return Resources.Online;
            default:
                return "";
        }
    }

    private _renderLastRunCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                {this._getLastRunElement(tableItem.agent)}
            </SimpleTableCell>
        );
    }

    private _getLastRunElement(agent: TaskAgent): JSX.Element {
        if (agent.assignedRequest) {
            return <span>{Resources.Now}</span>;
        }
        else if (agent.lastCompletedRequest) {
            if (agent.lastCompletedRequest.finishTime) {
                return <Ago date={agent.lastCompletedRequest.finishTime} />;
            }
            else if (agent.lastCompletedRequest.receiveTime) {
                return <Ago date={agent.lastCompletedRequest.receiveTime} />;
            }
            else if (agent.lastCompletedRequest.assignTime) {
                return <Ago date={agent.lastCompletedRequest.assignTime} />;
            }
            else {
                return <Ago date={agent.lastCompletedRequest.queueTime} />;
            }
        }
        else {
            return <span />;
        }
    }

    private _renderStatusCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        const agent: TaskAgent = tableItem.agent;
        if (agent.assignedRequest && agent.assignedRequest.planType && agent.assignedRequest.owner) {
            return (
                <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                    <Tooltip overflowOnly={true}>
                        <span className="text-ellipsis bolt-list-cell-text">
                            {format(Resources.RunningRequestInformation, agent.assignedRequest.planType.toLocaleLowerCase(), agent.assignedRequest.owner.name)}
                        </span>
                    </Tooltip>
                </SimpleTableCell>
            );
        } else if (agent.pendingUpdate && agent.pendingUpdate.currentState) {
            return (
                <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                    <Tooltip overflowOnly={true}>
                        <span className="text-ellipsis bolt-list-cell-text">
                            {agent.pendingUpdate.currentState}
                        </span>
                    </Tooltip>
                </SimpleTableCell>
            );
        } else if (agent.version && agent.version.search(/^1\.\d+/) >= 0) {
            return (
                <TwoLineTableCell
                    columnIndex={columnIndex}
                    tableColumn={tableColumn}
                    key={"col-" + columnIndex}
                    line1={Resources.DeprecatedAgent}
                    line2={<Link href={FwLinks.DeprecatedAgentHelpLink} target="_blank">{FwLinks.DeprecatedAgentHelpLink}</Link>}
                />
            );
        }
        else {
            return (
                <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                    <div>
                        {Resources.Idle}
                    </div>
                </SimpleTableCell>
            );
        }
    }

    private _renderVersionCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <Tooltip overflowOnly={true}>
                    <span className="text-ellipsis bolt-list-cell-text">
                        {tableItem.agent.version}
                    </span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _renderEnabledCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<TableListItem>, tableItem: TableListItem): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <Toggle
                    offText={Resources.Off}
                    onText={Resources.On}
                    checked={tableItem.agent.enabled}
                    onChange={(e, checked) => {
                        this.props.onAgentEnabledChanged(tableItem.agent, checked);
                        e.preventDefault();
                    }}
                />
            </SimpleTableCell>
        );
    }
}
